import { formatPlayerName } from './playerName';

/**
 * Match feed helpers.
 *
 * Pure functions that turn the raw commentary stream emitted by the match
 * engine into the shapes the redesigned match page renders:
 *  - a visual tier per row (quiet / medium / key / goal / band)
 *  - the accumulated scorer lines under the scoreboard
 *  - the pips on the 0-95' timeline strip
 *  - the current match minute
 *
 * The engine delivers a goal as FOUR separate commentary messages roughly
 * 1-1.5s apart (buildup, action, outcome, aftermath). Those arrive here as
 * separate rows carrying the same sequence_number and a `sub_type`, so every
 * helper below de-duplicates on the event identity rather than on the row.
 *
 * Gotcha: `team_context` is the team IN POSSESSION, not the team the named
 * player belongs to. It is reliable for goals and substitutions, but not for
 * fouls and cards, so card pips are placed on the centre line instead of
 * being attributed to a side.
 */

export const FEED_TIER = {
    BAND: 'band',
    GOAL: 'goal',
    KEY: 'key',
    MEDIUM: 'medium',
    QUIET: 'quiet',
    ABSORBED: 'absorbed'
};

export const FEED_FILTER = {
    ALL: 'all',
    KEY: 'key'
};

// Routine play. Rendered as a borderless one-liner in the feed.
const QUIET_EVENT_TYPES = new Set([
    'PASS_SUCCESS',
    'PASS_INTERCEPTED',
    'CLEARANCE',
    'FLOW_COMMENTARY',
    'COLOR_COMMENTARY',
    'HOLD_BALL',
    'GOAL_KICK',
    'LONG_BALL',
    'THROW_IN',
    'CORNER',
    'FREE_KICK',
    'FREE_KICK_AWARDED',
    'AERIAL_WON',
    'CROSS_FAIL',
    'CROSS_CLEARED',
    'TACKLE_FAIL',
    'POSITION_CHANGE',
    'SYSTEM_MESSAGE',
    'SYSTEM'
]);

// Attacking build-up worth a card, but not a headline.
const MEDIUM_EVENT_TYPES = new Set([
    'PASS_KEY',
    'PASS_SETUP',
    'CHANCE_CREATED',
    'OFFSIDE',
    'DRIBBLE_SUCCESS',
    'DRIBBLE_FAIL_TACKLED',
    'CROSS_SUCCESS',
    'AERIAL_DUEL',
    'DANGEROUS_ATTACK',
    'PRESSURE_BUILDING',
    'SOLO_RUN_SETUP',
    'TACKLE_SUCCESS',
    'SHOT_ATTEMPT',
    'SHOT_NARRATIVE',
    'SHOT_OFF_TARGET',
    'SHOT_BLOCKED',
    'HEADER_ATTEMPT'
]);

// Headline moments. Rendered as bordered narrative boxes.
const KEY_EVENT_TYPES = new Set([
    'SAVE',
    'PENALTY_SAVE',
    'PENALTY_MISS',
    'PENALTY_TAKEN',
    'PENALTY_AWARDED',
    'YELLOW_CARD',
    'RED_CARD',
    'FOUL',
    'FOUL_WARNING',
    'INJURY',
    'SUBSTITUTION',
    'TACTIC_CHANGE',
    'VAR_CHECK',
    'VAR_GOAL_DISALLOWED',
    'VAR_GOAL_CONFIRMED'
]);

const GOAL_EVENT_TYPES = new Set([
    'GOAL',
    'GOAL_NARRATIVE',
    'PENALTY_GOAL'
]);

const PERIOD_BAND_EVENT_TYPES = new Set([
    'KICKOFF',
    'HALF_TIME',
    'FULL_TIME'
]);

// Redundant with the goal hero card, which carries the score chip itself.
const ABSORBED_EVENT_TYPES = new Set(['SCORE_UPDATE']);

// Referee warnings are roughly three times as common as real yellow cards,
// so they must read calmer than a booking or they cry wolf all match.
const ACCENT_BY_EVENT_TYPE = {
    GOAL: 'goal',
    GOAL_NARRATIVE: 'goal',
    PENALTY_GOAL: 'goal',
    YELLOW_CARD: 'yellow',
    RED_CARD: 'red',
    FOUL_WARNING: 'warning',
    FOUL: 'warning',
    SAVE: 'save',
    PENALTY_SAVE: 'save',
    PENALTY_MISS: 'penalty',
    PENALTY_TAKEN: 'penalty',
    PENALTY_AWARDED: 'penalty',
    SUBSTITUTION: 'sub',
    INJURY: 'injury',
    VAR_CHECK: 'var',
    VAR_GOAL_DISALLOWED: 'var',
    VAR_GOAL_CONFIRMED: 'var'
};

const getFeedEventType = (event) => (
    event?.event_type || event?.type || 'SYSTEM_MESSAGE'
);

const getFeedEventSide = (event) => {
    const context = String(event?.team_context || '').toLowerCase();
    return context === 'home' || context === 'away' ? context : null;
};

const getFeedEventMinute = (event) => {
    const minute = Number(event?.minute);
    return Number.isFinite(minute) ? minute : 0;
};

/**
 * Identity for a single wire message. Distinguishes the four rows of one goal
 * narrative, so it is the right key for rows and period bands.
 * Saved history rows carry neither sequence_number nor timestamp, so they fall
 * back to their subdocument _id or hydration index. Without those, a goal
 * restart KICKOFF in the same minute as the half opener collides with it and
 * both rows render as period bands.
 */
export const getFeedEventIdentity = (event) => {
    if (event?.sequence_number != null) return `seq-${event.sequence_number}`;
    if (event?.timestamp) return `ts-${event.timestamp}`;
    if (event?._id != null) return `id-${String(event._id)}`;
    if (event?._history_index != null) return `hist-${event._history_index}`;
    return `min-${getFeedEventMinute(event)}-${getFeedEventType(event)}-${event?.player_name || ''}`;
};

/**
 * Identity for the underlying match event, shared by all of its narrative
 * parts. Live parts are emitted as separate wire messages that each get their
 * OWN sequence_number (the engine assigns numbers at emission, after the parts
 * are copied from the base event), so sequence_number cannot group them; saved
 * history fans out from one stored event and its parts share a number. This
 * grouped key covers both paths, which is what scorer and pip counting needs.
 */
export const getFeedEventGroupIdentity = (event) => (
    `${getFeedEventType(event)}-${getFeedEventMinute(event)}-${event?.player_name || ''}`
);

// Parts that precede the decisive moment of a multi-part narrative. Scorers
// and pips must not appear while the buildup is still building suspense.
const PRE_OUTCOME_SUBTYPES = new Set(['buildup', 'action']);

const isPreOutcomePart = (event) => PRE_OUTCOME_SUBTYPES.has(event?.sub_type);

const getFeedEventDescriptors = (event) => {
    const descriptors =
        event?._descriptors ||
        event?.narrative?.descriptors ||
        event?.narrative_data?.descriptors;
    return Array.isArray(descriptors) ? descriptors.filter(Boolean) : [];
};

export const isAbsorbedFeedEvent = (event) => ABSORBED_EVENT_TYPES.has(getFeedEventType(event));

const getFeedTier = (event) => {
    const type = getFeedEventType(event);
    if (ABSORBED_EVENT_TYPES.has(type)) return FEED_TIER.ABSORBED;
    if (PERIOD_BAND_EVENT_TYPES.has(type)) return FEED_TIER.BAND;
    if (GOAL_EVENT_TYPES.has(type)) return FEED_TIER.GOAL;
    if (KEY_EVENT_TYPES.has(type)) return FEED_TIER.KEY;
    if (MEDIUM_EVENT_TYPES.has(type)) return FEED_TIER.MEDIUM;
    if (QUIET_EVENT_TYPES.has(type)) return FEED_TIER.QUIET;
    return FEED_TIER.QUIET;
};

/**
 * Classifies a single commentary row for rendering.
 * Everything is derived from fields the engine already sends, so old matches
 * without any of the newer optional fields still classify correctly.
 */
export const classifyFeedEvent = (event) => {
    const type = getFeedEventType(event);
    const tier = getFeedTier(event);
    const isKeyMoment = tier === FEED_TIER.GOAL || tier === FEED_TIER.KEY || tier === FEED_TIER.BAND;

    return {
        type,
        tier,
        isKeyMoment,
        side: getFeedEventSide(event),
        subType: event?.sub_type || null,
        accent: ACCENT_BY_EVENT_TYPE[type] || null,
        minute: getFeedEventMinute(event),
        descriptors: getFeedEventDescriptors(event)
    };
};

const compareChronologically = (a, b) => {
    if (a?.sequence_number != null && b?.sequence_number != null) {
        return a.sequence_number - b.sequence_number;
    }
    const aTime = a?.timestamp ? new Date(a.timestamp).getTime() : NaN;
    const bTime = b?.timestamp ? new Date(b.timestamp).getTime() : NaN;
    if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
        return aTime - bTime;
    }
    return getFeedEventMinute(a) - getFeedEventMinute(b);
};

const toChronologicalOrder = (events) => (
    (Array.isArray(events) ? events : []).slice().sort(compareChronologically)
);

/**
 * The engine emits a KICKOFF for every restart, including after each goal.
 * Only the opening kickoff of each half deserves a period band.
 */
export const buildPeriodBandIdentitySet = (events) => {
    const bandIdentities = new Set();
    let awaitingHalfKickoff = true;

    toChronologicalOrder(events).forEach((event) => {
        const type = getFeedEventType(event);
        if (type === 'KICKOFF') {
            if (awaitingHalfKickoff) {
                bandIdentities.add(getFeedEventIdentity(event));
                awaitingHalfKickoff = false;
            }
            return;
        }
        if (type === 'HALF_TIME') {
            bandIdentities.add(getFeedEventIdentity(event));
            awaitingHalfKickoff = true;
            return;
        }
        if (type === 'FULL_TIME') {
            bandIdentities.add(getFeedEventIdentity(event));
        }
    });

    return bandIdentities;
};

/**
 * Accumulates the scorer lines shown under the scoreboard.
 * VAR_GOAL_DISALLOWED removes the matching scorer again.
 */
export const deriveScorers = (events) => {
    const scorers = { home: [], away: [] };
    const countedGoalIdentities = new Set();
    const processedVarIdentities = new Set();

    toChronologicalOrder(events).forEach((event) => {
        const type = getFeedEventType(event);
        const side = getFeedEventSide(event);

        if (GOAL_EVENT_TYPES.has(type)) {
            if (!side) return;
            if (isPreOutcomePart(event)) return;
            const identity = getFeedEventGroupIdentity(event);
            if (countedGoalIdentities.has(identity)) return;
            countedGoalIdentities.add(identity);
            scorers[side].push({
                name: event?.player_name || 'Unknown',
                minute: getFeedEventMinute(event),
                assist: event?.secondary_player_name || null,
                isPenalty: type === 'PENALTY_GOAL'
            });
            return;
        }

        if (type === 'VAR_GOAL_DISALLOWED') {
            const identity = getFeedEventGroupIdentity(event);
            if (processedVarIdentities.has(identity)) return;
            processedVarIdentities.add(identity);
            const disallowedName = event?.player_name || null;
            const sides = side ? [side] : ['home', 'away'];
            for (const candidateSide of sides) {
                const list = scorers[candidateSide];
                const index = disallowedName
                    ? list.map((entry) => entry.name).lastIndexOf(disallowedName)
                    : list.length - 1;
                if (index >= 0) {
                    list.splice(index, 1);
                    break;
                }
            }
        }
    });

    return scorers;
};

// One line per scorer, minutes accumulated: "Okafor 23' 67'", "Moreau 51' (P)".
export const groupScorerLines = (entries) => {
    const byName = new Map();
    entries.forEach((entry) => {
        if (!byName.has(entry.name)) byName.set(entry.name, []);
        byName.get(entry.name).push(`${entry.minute}'${entry.isPenalty ? ' (P)' : ''}`);
    });
    return Array.from(byName, ([name, minutes]) => ({
        name,
        text: `${formatPlayerName(name) || 'Unknown'} ${minutes.join(' ')}`
    }));
};

/**
 * The page has no separate match clock: the current minute is whatever the
 * newest commentary event reported.
 */
export const getLatestMinute = (events) => {
    let latest = 0;
    (Array.isArray(events) ? events : []).forEach((event) => {
        const minute = getFeedEventMinute(event);
        if (minute > latest) latest = minute;
    });
    return latest;
};

export const getLatestFormalMinute = (events) => {
    const ordered = toChronologicalOrder(events);
    for (let index = ordered.length - 1; index >= 0; index -= 1) {
        const formalMinute = ordered[index]?.formal_minute;
        if (formalMinute != null && String(formalMinute).trim() !== '') {
            return String(formalMinute).trim();
        }
    }
    return null;
};

/**
 * True once the engine has told us the first half is over and the second half
 * has not restarted yet.
 */
export const isHalfTimeBreak = (events) => {
    const ordered = toChronologicalOrder(events);
    for (let index = ordered.length - 1; index >= 0; index -= 1) {
        const type = getFeedEventType(ordered[index]);
        if (type === 'HALF_TIME') return true;
        if (type === 'KICKOFF' || type === 'FULL_TIME') return false;
    }
    return false;
};

export default {
    FEED_TIER,
    FEED_FILTER,
    classifyFeedEvent,
    deriveScorers,
    groupScorerLines,
    getLatestMinute,
    getLatestFormalMinute,
    buildPeriodBandIdentitySet,
    isAbsorbedFeedEvent,
    isHalfTimeBreak
};
